import React, { useMemo } from "react";
import { addYears, subYears } from "date-fns";
import { useTranslation } from "react-i18next";
import { Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { RangeSelector } from "@rentablo/components";
import { useCashflowAnalyzerContext } from "@providers";
import { Chips, FilterItem } from "@components/filters";
import { BarChartMode, BarChartModes as barChartModes } from "../types";

const intervalTypes: IntervalType[] = ["YTD", "oneYear", "threeYears", "all"];

type ButtonsProps = {
  handleChange: (direction: "back" | "forward") => void;
  isBackDisabled: boolean;
  isForwardDisabled: boolean;
};

const Buttons = ({
  handleChange,
  isBackDisabled,
  isForwardDisabled,
}: ButtonsProps) => {
  return (
    <Box display="flex" alignItems="center" justifyContent="flex-end" gap={3}>
      <IconButton
        disabled={isBackDisabled}
        onClick={() => handleChange("back")}
        size="small"
        color="secondary"
      >
        <KeyboardArrowLeftIcon />
      </IconButton>
      <IconButton
        disabled={isForwardDisabled}
        onClick={() => handleChange("forward")}
        size="small"
        color="secondary"
      >
        <KeyboardArrowRightIcon />
      </IconButton>
    </Box>
  );
};

type Props = {
  onMainPeriodChange: (period: Required<RangeType>) => void;
  isFetching: boolean;
};

export const BarChartToolbar = ({ onMainPeriodChange, isFetching }: Props) => {
  const {
    mainPeriod,
    intervalType,
    mode,
    onModeChange,
    disabledIntervals,
    selectInterval,
  } = useCashflowAnalyzerContext();
  const theme = useTheme();
  const { t } = useTranslation();
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const isLess450 = useMediaQuery("(max-width: 450px)");
  const endYear = mainPeriod.endDate.getFullYear();
  const isForwardDisabled =
    endYear + 1 > new Date().getFullYear() || intervalType === "all";
  const isBackwardsDisabled = intervalType === "all";

  const handleChange = (direction: "back" | "forward") => {
    if (isForwardDisabled && direction === "forward") {
      return;
    }

    const yearOffset = intervalType === "threeYears" ? 3 : 1;
    if (direction === "forward") {
      onMainPeriodChange({
        startDate: addYears(mainPeriod.startDate, yearOffset),
        endDate: addYears(mainPeriod.endDate, yearOffset),
      });
    } else {
      onMainPeriodChange({
        startDate: subYears(mainPeriod.startDate, yearOffset),
        endDate: subYears(mainPeriod.endDate, yearOffset),
      });
    }
  };

  const items = useMemo(
    () =>
      [...barChartModes].map<FilterItem>((i) => ({
        text: t(`cashflowAnalyzer.${i}`),
        value: i as BarChartMode,
        clickable: true,
      })),
    [t]
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems={isMd ? "flex-start" : "flex-end"}
      width={isLess450 ? "100%" : undefined}
    >
      {isMd && (
        <Box pb={theme.spacing(2.5)}>
          <Buttons
            handleChange={handleChange}
            isBackDisabled={false}
            isForwardDisabled={isForwardDisabled}
          />
        </Box>
      )}
      <Chips
        options={items}
        selectedOptions={[mode]}
        singleSelectMode
        size="small"
        setSelectedOptions={(options) =>
          onModeChange(
            options.length > 0 ? (options[0] as BarChartMode) : "monthly"
          )
        }
      />
      <Box
        display="flex"
        justifyContent="flex-end"
        gap={2.5}
        sx={{
          flexDirection: "row",
          position: "relative",
          right: 0,
          marginTop: theme.spacing(1.5),
        }}
      >
        <RangeSelector
          intervalType={intervalType}
          intervalTypes={intervalTypes}
          onSelect={selectInterval}
          disabledIntervalTypes={isFetching ? intervalTypes : disabledIntervals}
        />
        {!isMd && (
          <Buttons
            handleChange={handleChange}
            isBackDisabled={isFetching ? true : isBackwardsDisabled}
            isForwardDisabled={isFetching ? true : isForwardDisabled}
          />
        )}
      </Box>
    </Box>
  );
};
